import { defineStore } from 'pinia'
import type {
  ArticleMetaData,
  ArticleState,
  MetaDataCache,
  IngredientLaravel,
  Ingredients,
} from '@/types/article-types'

export const useArticlesStore = defineStore('articles', {
  state: (): ArticleState => ({
    mode: 'original', // original|personalized
    cache: {}, // see flow type
    scaleLoading: false,
    scaleError: '',
    metaData: {
      mealType: '',
      dietaryType: '',
      prepTime: null,
      cookTime: null,
      servingSize: null,
      calories: null,
      fat: null,
      carbs: null,
      protein: null,
    },
    ingredients: {},
  }),

  getters: {
    /**
     * Helper for ingredient-list.vue so that qty can be updated dynamically
     * when a recipe is scaled.
     */
    getScaledIngredients: (state: ArticleState) => (ingredients: Array<IngredientLaravel>) => {
      // scaled mode
      // return new array with updated quantities

      if (state.mode === 'scaled' && state.ingredients) {
        return ingredients.map((ingredient) => ({
          ...ingredient,

          // Scaling API returns camelcase, while the controller/model
          // returns snake-case. Properties here to handle both cases
          // when the recipe is scaled.

          // scaled properties
          metric_qty: state.ingredients[ingredient.id]
            ? state.ingredients[ingredient.id].metricQty
            : ingredient.metric_qty,
          imperial_qty: state.ingredients[ingredient.id]
            ? state.ingredients[ingredient.id].imperialQty
            : ingredient.imperial_qty,

          // whole unit properties
          whole_qty: state.ingredients[ingredient.id]
            ? state.ingredients[ingredient.id].wholeQty
            : ingredient.whole_qty,
          whole_unit_name: state.ingredients[ingredient.id]
            ? state.ingredients[ingredient.id].wholeUnitName
            : ingredient.whole_unit_name,
        }))
      }

      // no changes, not scaled
      return ingredients
    },
  },

  actions: {
    /**
     * Helper to set the entire recipe into scaled mode with updated
     * metadata and ingredient quantities.
     *
     * Assumes data is already in cache
     */
    scaleRecipe() {
      if (this.cache && this.cache.scaledMetaData && this.cache.scaledIngredients) {
        this.updateMetaData(this.cache.scaledMetaData)
        this.setIngredients(this.cache.scaledIngredients)
        this.setMode('scaled')
      }
    },

    /**
     * Helper to revert the un-scale / revert the recipe back to original
     * data.
     *
     * Assumes original data is already in cache.
     */
    resetRecipe() {
      if (this.cache && this.cache.originalMetaData) {
        this.updateMetaData(this.cache.originalMetaData)

        // clear out ingredients and the getter getScaledIngredients
        // will simply return the original ingredients
        this.setIngredients({})

        // toggle mode
        this.setMode('original')
      }
    },

    // MUTATIONS
    // original, scaled
    setMode(mode: string) {
      this.mode = mode
    },

    // originalMetaData, metaData, ingredients
    updateCache(cache: MetaDataCache) {
      this.cache = {
        ...this.cache,
        ...cache,
      }
    },

    // overwrite meta data, keeping original if not overwritten
    updateMetaData(metaData: ArticleMetaData) {
      this.metaData = {
        ...this.metaData,
        ...metaData,
      }
    },

    // map of ingredients { id: {}, id: {} } for easy lookup
    setIngredients(ingredients: Ingredients) {
      this.ingredients = {
        ...ingredients,
      }
    },

    setScaleLoading(newLoading: boolean) {
      this.scaleLoading = newLoading
    },

    setScaleError(error: string) {
      this.scaleError = error
    },
  },
})
